import React, { useEffect, useState, Fragment } from 'react';
import DashboardLayout from '../components/Nav';
import toast from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { PencilIcon, TrashIcon, PlusIcon, XMarkIcon, SignalIcon } from '@heroicons/react/24/outline';
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();

const emptyForm = { name: '', base_url: '', username: '', password: '', description: '', active: true };

export default function RadiusSourceManagementPage() {
  const [sources, setSources] = useState([]);
  const [newSource, setNewSource] = useState(emptyForm);
  const [editSource, setEditSource] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [token, setToken] = useState('');

  useEffect(() => {
    document.title = "RADIUS Sources | IP Intelligence Portal";
  }, []);

  useEffect(() => {
    const auth = localStorage.getItem('auth_token');
    if (!auth) return;
    setToken(auth);
  }, []);

  useEffect(() => {
    if (token) fetchSources();
  }, [token]);

  const fetchSources = () => {
    fetch(`${API_BASE_URL}/radius/sources`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setSources(data?.data || []));
  };

  const call = async (endpoint, method = 'POST', body) => {
    const res = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const data = res.status === 204 ? {} : await res.json();
    if (!res.ok) throw new Error(data?.error || 'Something went wrong');
    return data;
  };

  const handleAdd = async () => {
    try {
      await call('radius/sources', 'POST', newSource);
      toast.success('RADIUS source added');
      setNewSource(emptyForm);
      setIsAddModalOpen(false);
      fetchSources();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = async () => {
    try {
      const { id, password, ...rest } = editSource;
      const payload = { ...rest };
      if (password) payload.password = password; // only send if changed
      await call(`radius/sources/${id}`, 'PUT', payload);
      toast.success('RADIUS source updated');
      setEditSource(null);
      setIsEditModalOpen(false);
      fetchSources();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await call(`radius/sources/${id}`, 'DELETE');
      toast.success('RADIUS source deleted');
      fetchSources();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const data = await call(`radius/sources/${id}/test`, 'POST', {});
      if (data.success) {
        toast.success(data.message || 'Connection successful');
      } else {
        toast.error(data.error || 'Connection failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">RADIUS Sources</h2>
          <button
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            onClick={() => setIsAddModalOpen(true)}
          >
            <PlusIcon className="w-5 h-5" />
            Add Source
          </button>
        </div>

        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Base URL</th>
              <th className="p-3 text-left">Username</th>
              <th className="p-3 text-center">Active</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map(src => (
              <tr key={src.id} className="border-t hover:bg-gray-50">
                <td className="p-3">{src.name}</td>
                <td className="p-3 font-mono text-xs">{src.base_url}</td>
                <td className="p-3">{src.username}</td>
                <td className="p-3 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${src.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {src.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-3 text-center space-x-2">
                  <button
                    className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                    title="Test connection"
                    disabled={testingId === src.id}
                    onClick={() => handleTest(src.id)}
                  >
                    <SignalIcon className={`w-5 h-5 inline ${testingId === src.id ? 'animate-pulse' : ''}`} />
                  </button>
                  <button
                    className="text-blue-600 hover:text-blue-800"
                    onClick={() => {
                      setEditSource({ ...src, password: '' });
                      setIsEditModalOpen(true);
                    }}
                  >
                    <PencilIcon className="w-5 h-5 inline" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800"
                    onClick={() => handleDelete(src.id)}
                  >
                    <TrashIcon className="w-5 h-5 inline" />
                  </button>
                </td>
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-400">No RADIUS sources configured</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      <Transition appear show={isAddModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsAddModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title className="text-lg font-semibold">Add RADIUS Source</Dialog.Title>
                <XMarkIcon className="w-5 h-5 cursor-pointer" onClick={() => setIsAddModalOpen(false)} />
              </div>
              <div className="space-y-3">
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Name"
                  value={newSource.name}
                  onChange={e => setNewSource({ ...newSource, name: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Base URL (e.g. http://panel.zalpro.com/api)"
                  value={newSource.base_url}
                  onChange={e => setNewSource({ ...newSource, base_url: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Username"
                  value={newSource.username}
                  onChange={e => setNewSource({ ...newSource, username: e.target.value })}
                />
                <input
                  type="password"
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Password"
                  value={newSource.password}
                  onChange={e => setNewSource({ ...newSource, password: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Description (optional)"
                  value={newSource.description}
                  onChange={e => setNewSource({ ...newSource, description: e.target.value })}
                />
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={newSource.active}
                    onChange={e => setNewSource({ ...newSource, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  onClick={handleAdd}
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Modal */}
      <Transition appear show={isEditModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsEditModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title className="text-lg font-semibold">Edit RADIUS Source</Dialog.Title>
                <XMarkIcon className="w-5 h-5 cursor-pointer" onClick={() => setIsEditModalOpen(false)} />
              </div>
              <div className="space-y-3">
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Name"
                  value={editSource?.name || ''}
                  onChange={e => setEditSource({ ...editSource, name: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Base URL"
                  value={editSource?.base_url || ''}
                  onChange={e => setEditSource({ ...editSource, base_url: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Username"
                  value={editSource?.username || ''}
                  onChange={e => setEditSource({ ...editSource, username: e.target.value })}
                />
                <input
                  type="password"
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Password (leave blank to keep unchanged)"
                  value={editSource?.password || ''}
                  onChange={e => setEditSource({ ...editSource, password: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Description (optional)"
                  value={editSource?.description || ''}
                  onChange={e => setEditSource({ ...editSource, description: e.target.value })}
                />
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={!!editSource?.active}
                    onChange={e => setEditSource({ ...editSource, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  onClick={handleEdit}
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </DashboardLayout>
  );
}